import React, { useState, useEffect } from 'react';
import { Link, Element } from 'react-scroll';

const About = () => {
  // State for tracking window width
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  
  // Determine if mobile based on window width
  const isMobile = windowWidth < 768;
  
  // Add event listener for window resize
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    
    // Add event listener
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      
      // Call handler right away so state gets updated with initial window size
      handleResize();
      
      // Remove event listener on cleanup
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []); // Empty array ensures effect runs only on mount and unmount

  // Define all styles inline to avoid any CSS conflicts
  const styles = {
    section: {
      padding: '80px 0',
      backgroundColor: '#f8fafc',
      backgroundImage: 'linear-gradient(120deg, #f8fafc 0%, #f0f7ff 100%)',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px'
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginRight: '-15px',
      marginLeft: '-15px'
    },
    column: {
      flex: '0 0 50%',
      maxWidth: '50%',
      padding: '0 15px',
      marginBottom: '40px'
    },
    columnMobile: {
      flex: '0 0 100%',
      maxWidth: '100%',
      padding: '0 15px',
      marginBottom: '40px'
    },
    headingContainer: {
      marginBottom: '25px',
      position: 'relative'
    },
    subHeading: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1e50a2',
      marginBottom: '10px',
      display: 'inline-block',
      position: 'relative',
      paddingLeft: '40px',
    },
    subHeadingLine: {
      position: 'absolute',
      width: '30px',
      height: '2px',
      backgroundColor: '#1e50a2',
      left: '0',
      top: '50%',
    },
    heading: {
      fontSize: isMobile ? '36px' : '48px',
      fontWeight: 'bold',
      lineHeight: '1.1',
      backgroundImage: 'linear-gradient(135deg, #0a1438 0%, #1e50a2 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      marginBottom: '25px'
    },
    description: {
      fontSize: '18px',
      lineHeight: '1.7',
      color: '#4a5568',
      marginBottom: '30px',
      maxWidth: '90%'
    },
    ctaButton: {
      padding: '12px 28px',
      fontSize: '16px',
      fontWeight: 'bold',
      backgroundColor: '#1e50a2',
      color: 'white',
      border: 'none',
      borderRadius: '30px',
      cursor: 'pointer',
      boxShadow: '0 4px 15px rgba(30, 80, 162, 0.3)',
      transition: 'all 0.3s ease',
      display: 'inline-block',
      marginTop: '10px',
    },
    dashboard: {
      backgroundColor: 'white',
      padding: '25px',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      transform: isMobile ? 'none' : 'perspective(1000px) rotateY(-5deg)',
      transition: 'all 0.5s ease',
      position: 'relative',
      zIndex: 1,
      overflow: 'hidden',
    },
    dashboardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '25px',
      paddingBottom: '15px',
      borderBottom: '1px solid #f0f0f0'
    },
    headerText: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1e50a2'
    },
    headerActions: {
      display: 'flex',
      gap: '10px',
    },
    actionDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#e2e8f0',
    },
    dataRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '25px',
      gap: '20px'
    },
    dataBox: {
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '15px',
      width: '48%',
      transition: 'all 0.3s ease',
      border: '1px solid #f0f7ff',
    },
    dataBoxHover: {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.08)',
    },
    boxTitle: {
      fontSize: '22px',
      fontWeight: 'bold',
      margin: '0 0 10px 0',
      color: '#0a1438'
    },
    growth: {
      color: '#4caf50',
      margin: '5px 0',
      fontSize: '15px',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    },
    label: {
      color: '#718096',
      fontSize: '14px',
      margin: '5px 0 0 0'
    },
    percentage: {
      fontSize: '26px',
      fontWeight: 'bold',
      color: '#0a1438'
    },
    progressContainer: {
      backgroundColor: '#e2e8f0',
      height: '8px',
      borderRadius: '4px',
      marginTop: '10px',
      overflow: 'hidden'
    },
    progressBar: {
      width: '89.5%',
      height: '100%',
      backgroundColor: '#1e50a2',
      borderRadius: '4px',
    },
    avatars: {
      display: 'flex',
      marginTop: '25px',
      alignItems: 'center'
    },
    avatarsText: {
      marginLeft: '15px',
      fontSize: '14px',
      color: '#718096'
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: '2px solid white',
      marginRight: '-12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    featuresRow: {
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: '60px',
      gap: '20px',
      justifyContent: 'center'
    },
    featuresHeader: {
      textAlign: 'center',
      marginBottom: '40px',
      width: '100%'
    },
    featuresTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#0a1438',
      marginBottom: '15px'
    },
    featuresDescription: {
      fontSize: '18px',
      color: '#4a5568',
      maxWidth: '700px',
      margin: '0 auto'
    },
    feature: {
      flex: isMobile ? '0 0 100%' : '0 0 calc(33.333% - 20px)',
      marginBottom: '20px'
    },
    featureBox: {
      backgroundColor: 'white',
      padding: '30px',
      borderRadius: '15px',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
      height: '100%',
      transition: 'all 0.3s ease',
      border: '1px solid #f0f0f0',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    },
    featureBoxHover: {
      transform: 'translateY(-10px)',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
      borderColor: '#e2e8f0'
    },
    featureIcon: {
      width: '50px',
      height: '50px',
      borderRadius: '12px',
      backgroundColor: '#f0f7ff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
      color: '#1e50a2',
      fontSize: '24px'
    },
    featureTitle: {
      color: '#0a1438',
      marginTop: '0',
      marginBottom: '15px',
      fontSize: '20px',
      fontWeight: 'bold'
    },
    featureText: {
      color: '#4a5568',
      fontSize: '16px',
      lineHeight: '1.6',
      margin: '0'
    },
    featureAccent: {
      position: 'absolute',
      width: '100px',
      height: '100px',
      borderRadius: '50%',
      backgroundColor: 'rgba(30, 80, 162, 0.05)',
      bottom: '-50px',
      right: '-50px',
      zIndex: 0
    }
  };

  // State for hover effects
  const [hoveredBox, setHoveredBox] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);

  // Feature data
  const features = [
    {
      id: 1,
      title: "Local Expertise",
      text: "Our deep understanding of African markets ensures you get access to the best opportunities.",
      icon: "🌍"
    },
    {
      id: 2,
      title: "Secure & Regulated",
      text: "We prioritize your safety with state-of-the-art encryption and compliance with all regulatory standards.",
      icon: "🔒"
    },
    {
      id: 3,
      title: "Transparent Fees",
      text: "No hidden charges—know exactly what you're paying for with our clear fee structure.",
      icon: "💎"
    },
    {
      id: 4,
      title: "Accessible to All",
      text: "Start small and grow at your own pace, with investments that fit any budget, starting from just $100.",
      icon: "🚀"
    },
    {
      id: 5,
      title: "Customer Support",
      text: "Our friendly support team is here to assist you every step of the way, 24/7.",
      icon: "👋"
    },
    {
      id: 6,
      title: "Financial Education",
      text: "Access free resources to improve your financial literacy and make informed investment decisions.",
      icon: "📚"
    }
  ];

  return (
    <section id="about" style={styles.section}>
      <div style={styles.container}>
        <div style={styles.row}>
          {/* Left column: Text content */}
          <div style={isMobile ? styles.columnMobile : styles.column}>
            <div style={styles.headingContainer}>
              <div style={styles.subHeading}>
                <span style={styles.subHeadingLine}></span>
                About Laki
              </div>
              <h2 style={styles.heading}>
                Rethink money management
              </h2>
            </div>
            <p style={styles.description}>
              At Laki, we empower individuals to grow their wealth through smart and accessible investments in money market funds across Africa. Our platform is designed to provide a seamless experience for beginners and seasoned investors alike.
            </p>
            {/* <button style={styles.ctaButton}>
              Start Investing Today
            </button> */}
          </div>
          
          {/* Right column: App data */}
          <div style={isMobile ? styles.columnMobile : styles.column}>
            <div style={styles.dashboard}>
              <div style={styles.dashboardHeader}>
                <span style={styles.headerText}>My Portfolio</span>
                <div style={styles.headerActions}>
                  <div style={{...styles.actionDot, backgroundColor: '#f56565'}}></div>
                  <div style={{...styles.actionDot, backgroundColor: '#f6ad55'}}></div>
                  <div style={{...styles.actionDot, backgroundColor: '#48bb78'}}></div>
                </div>
              </div>
              
              <div style={styles.dataRow}>
                <div 
                  style={{
                    ...styles.dataBox, 
                    ...(hoveredBox === 1 ? styles.dataBoxHover : {})
                  }}
                  onMouseEnter={() => setHoveredBox(1)}
                  onMouseLeave={() => setHoveredBox(null)}
                >
                  <h3 style={styles.boxTitle}>$2,680.00</h3>
                  <p style={styles.growth}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 8L12 4L16 8" stroke="#4caf50" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 4L12 20" stroke="#4caf50" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    23% growth
                  </p>
                  <p style={styles.label}>Investment Value</p>
                </div>
                
                <div 
                  style={{
                    ...styles.dataBox, 
                    ...(hoveredBox === 2 ? styles.dataBoxHover : {})
                  }}
                  onMouseEnter={() => setHoveredBox(2)}
                  onMouseLeave={() => setHoveredBox(null)}
                >
                  <h3 style={styles.boxTitle}>Nairobi MMF</h3>
                  <p style={styles.growth}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 8L12 4L16 8" stroke="#4caf50" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 4L12 20" stroke="#4caf50" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    23% growth
                  </p>
                  <p style={styles.label}>Top Performing Fund</p>
                </div>
              </div>
              
              <div style={{...styles.dataRow, alignItems: 'center'}}>
                <p style={{color: '#4a5568', margin: 0}}>Total invested</p>
                <p style={styles.percentage}>89.5%</p>
              </div>
              
              <div style={styles.progressContainer}>
                <div style={styles.progressBar}></div>
              </div>
              
              <div style={styles.avatars}>
                <img 
                  src="https://randomuser.me/api/portraits/men/32.jpg" 
                  alt="User" 
                  style={{...styles.avatar, zIndex: 3}} 
                />
                <img 
                  src="https://randomuser.me/api/portraits/women/44.jpg" 
                  alt="User" 
                  style={{...styles.avatar, zIndex: 2}} 
                />
                <img 
                  src="https://randomuser.me/api/portraits/women/65.jpg" 
                  alt="User" 
                  style={{...styles.avatar, zIndex: 1}} 
                />
                <span style={styles.avatarsText}>Join 10,000+ investors</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Features section */}
        <div style={styles.featuresRow}>
          <div style={styles.featuresHeader}>
            <h3 style={styles.featuresTitle}>Why Choose Laki</h3>
            <p style={styles.featuresDescription}>
              We're dedicated to making investment accessible, transparent, and rewarding for everyone.
            </p>
          </div>
          
          {features.map(feature => (
            <div style={styles.feature} key={feature.id}>
              <div 
                style={{
                  ...styles.featureBox,
                  ...(hoveredFeature === feature.id ? styles.featureBoxHover : {})
                }}
                onMouseEnter={() => setHoveredFeature(feature.id)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h4 style={styles.featureTitle}>{feature.title}</h4>
                <p style={styles.featureText}>{feature.text}</p>
                <div style={styles.featureAccent}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;